<template>
  <div id="agenda" class="agenda-section px-4">
    <div class="agenda-section__card">
      <!-- Section header — matches FAQ / Speakers / Sponsors chrome. -->
      <div class="agenda-section__header">
        <h2 class="agenda-section__title">
          {{ caption || $t('Views.Lobby.AgendaSection.defaultCaption') }}
        </h2>
      </div>

      <!-- Search + filters — matches the Program page filter row. -->
      <div class="prog-filters">
        <!-- Search -->
        <div class="prog-filter prog-filter--search">
          <i
            class="fas fa-search prog-filter__search-icon"
            aria-hidden="true"
          ></i>
          <input
            v-model="searchText"
            class="prog-filter__search"
            :placeholder="$t('Components.Filters.searchPlaceholder')"
            :aria-label="$t('Views.Lobby.AgendaSection.searchSessions')"
          />
        </div>

        <!-- Room / track -->
        <div
          v-if="tracks && tracks.length > 1"
          class="prog-filter prog-filter--first-pill"
        >
          <span class="prog-filter__label">{{
            $t('Views.Lobby.AgendaSection.room')
          }}</span>
          <button
            type="button"
            class="prog-filter__trigger"
            :class="{ 'prog-filter__trigger--open': roomPickerOpen }"
            @click.stop="toggleRoomPicker"
            @keydown.escape="roomPickerOpen = false"
            aria-haspopup="listbox"
            :aria-expanded="roomPickerOpen ? 'true' : 'false'"
            :aria-label="$t('Views.Lobby.AgendaSection.filterByRoom')"
          >
            <span class="prog-filter__value">{{
              selectedRoomLabel(tracks)
            }}</span>
            <i
              class="fas fa-chevron-down prog-filter__chevron"
              :class="{ 'prog-filter__chevron--open': roomPickerOpen }"
              aria-hidden="true"
            ></i>
          </button>
          <transition name="notif-fade">
            <div
              v-if="roomPickerOpen"
              class="prog-filter__panel"
              role="listbox"
              :aria-label="$t('Views.Lobby.AgendaSection.selectRoom')"
              @click.stop
            >
              <button
                type="button"
                class="prog-filter__option"
                :class="{ 'prog-filter__option--active': !selectedTrackId }"
                role="option"
                :aria-selected="!selectedTrackId ? 'true' : 'false'"
                @click="
                  selectedTrackId = null;
                  roomPickerOpen = false;
                "
              >
                {{ $t('Views.Lobby.AgendaSection.allRooms') }}
                <i
                  v-if="!selectedTrackId"
                  class="fas fa-check prog-filter__option-check"
                ></i>
              </button>
              <button
                v-for="track in tracks"
                :key="track.id"
                type="button"
                class="prog-filter__option"
                :class="{
                  'prog-filter__option--active': selectedTrackId === track.id
                }"
                role="option"
                :aria-selected="selectedTrackId === track.id ? 'true' : 'false'"
                @click="
                  selectedTrackId = track.id;
                  roomPickerOpen = false;
                "
              >
                {{ track.title }}
                <i
                  v-if="selectedTrackId === track.id"
                  class="fas fa-check prog-filter__option-check"
                ></i>
              </button>
            </div>
          </transition>
        </div>

        <!-- Category -->
        <div
          v-if="allCategories.length"
          class="prog-filter prog-filter--category"
          :class="{ 'prog-filter--first-pill': !(tracks && tracks.length > 1) }"
        >
          <span class="prog-filter__label">{{
            $t('Views.Lobby.AgendaSection.category')
          }}</span>
          <button
            type="button"
            class="prog-filter__trigger"
            :class="{ 'prog-filter__trigger--open': categoryPickerOpen }"
            @click.stop="toggleCategoryPicker"
            @keydown.escape="categoryPickerOpen = false"
            aria-haspopup="listbox"
            :aria-expanded="categoryPickerOpen ? 'true' : 'false'"
            :aria-label="$t('Views.Lobby.AgendaSection.filterByCategory')"
          >
            <span class="prog-filter__value">{{ selectedCategoryLabel }}</span>
            <i
              class="fas fa-chevron-down prog-filter__chevron"
              :class="{ 'prog-filter__chevron--open': categoryPickerOpen }"
              aria-hidden="true"
            ></i>
          </button>
          <transition name="notif-fade">
            <div
              v-if="categoryPickerOpen"
              class="prog-filter__panel"
              role="listbox"
              :aria-label="$t('Views.Lobby.AgendaSection.selectCategory')"
              @click.stop
            >
              <button
                type="button"
                class="prog-filter__option"
                :class="{ 'prog-filter__option--active': !selectedCategory }"
                role="option"
                :aria-selected="!selectedCategory ? 'true' : 'false'"
                @click="
                  selectedCategory = null;
                  categoryPickerOpen = false;
                "
              >
                {{ $t('Views.Lobby.AgendaSection.allCategories') }}
                <i
                  v-if="!selectedCategory"
                  class="fas fa-check prog-filter__option-check"
                ></i>
              </button>
              <button
                v-for="cat in allCategories"
                :key="cat"
                type="button"
                class="prog-filter__option"
                :class="{
                  'prog-filter__option--active': selectedCategory === cat
                }"
                role="option"
                :aria-selected="selectedCategory === cat ? 'true' : 'false'"
                @click="
                  selectedCategory = cat;
                  categoryPickerOpen = false;
                "
              >
                {{ cat }}
                <i
                  v-if="selectedCategory === cat"
                  class="fas fa-check prog-filter__option-check"
                ></i>
              </button>
            </div>
          </transition>
        </div>

        <!-- Clear -->
        <button
          v-if="selectedTrackId || selectedCategory"
          type="button"
          class="prog-filter__clear"
          @click="clearFilters"
          :aria-label="$t('Views.Lobby.AgendaSection.clearAllFilters')"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
          {{ $t('Views.Lobby.AgendaSection.clear') }}
        </button>

        <!-- Download agenda as PDF — hidden on the lobby (kept in the markup
             so it can be re-enabled). The Program page has its own button. -->
        <button
          v-show="false"
          type="button"
          class="prog-filter__download"
          :aria-busy="isDownloadingPdf ? 'true' : 'false'"
          @click="handleDownloadAgenda"
        >
          <i
            class="fas"
            :class="isDownloadingPdf ? 'fa-spinner fa-spin' : 'fa-download'"
            aria-hidden="true"
          ></i>
          {{ $t('Views.Stage.Agenda.downloadAgenda') }}
        </button>
      </div>
      <p
        v-if="downloadError"
        class="agenda-section__download-error"
        role="alert"
      >
        {{ $t('Views.Stage.Agenda.downloadAgendaFailed') }}
      </p>

      <!-- Loading -->
      <div v-if="isLoading" class="agenda-section__loading">
        <i class="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
      </div>

      <!-- Day groups -->
      <CollapsibleList v-else>
        <CollapsibleItem
          v-slot="{ active, toggle }"
          :item-id="date"
          :agenda="singleAgendaGroupedByDay"
          v-for="(day, date) in displayedItems.sessions"
          :key="date"
        >
          <div
            class="agenda-day"
            :class="{
              'agenda-day--open': active,
              'agenda-day--last': lastVisibleDate === date
            }"
          >
            <!-- Day toggle -->
            <button
              class="agenda-day__toggle focus:outline-none"
              @click="toggle"
            >
              <span class="agenda-day__date">
                <Moment
                  v-if="locale === 'en'"
                  :value="date"
                  format="dddd DD MMMM"
                />
                <Moment
                  v-if="locale === 'nb'"
                  :value="date"
                  format="dddd DD. MMMM"
                />
              </span>
              <i
                class="agenda-day__chevron far"
                :class="{
                  'fa-chevron-up': active,
                  'fa-chevron-down': !active
                }"
                aria-hidden="true"
              />
            </button>

            <!-- Sessions for the day -->
            <div v-if="active" class="agenda-day__sessions">
              <div class="session-grid">
                <div
                  class="session-card"
                  v-for="session in day"
                  :key="session.id"
                  :class="{ 'session-card--ended': isPassed(session) }"
                >
                  <!-- Left: time + status -->
                  <div class="session-card__time-col">
                    <i
                      class="far fa-clock session-card__clock"
                      aria-hidden="true"
                    ></i>
                    <span class="session-card__start">{{
                      formatAgendaTime(session.startDate)
                    }}</span>
                    <span class="session-card__end">{{
                      formatAgendaTime(session.endDate)
                    }}</span>
                    <!-- Available / Ended tag — hidden (kept in the markup so it
                           can be re-enabled). -->
                    <span
                      v-show="false"
                      class="session-card__status"
                      :class="getStatusClass(session, bookSessions)"
                    >
                      <i
                        v-if="isLocked(session, bookSessions)"
                        class="fas fa-lock"
                        aria-hidden="true"
                      ></i>
                      {{ getStatusLabel(session, bookSessions) }}
                    </span>
                  </div>

                  <!-- Right: info -->
                  <div class="session-card__body">
                    <!-- Title row — title + inline category chips -->
                    <div class="session-card__title-row">
                      <h3 class="session-card__title">
                        <span
                          v-if="isLive(session) && !isPassed(session)"
                          class="session-card__live-dot"
                          role="img"
                          :aria-label="$t('Views.Program.statusLive')"
                          :title="$t('Views.Program.statusLive')"
                        ></span>
                        {{ session.title }}
                      </h3>
                      <span
                        v-for="(cat, ci) in sessionCategories(session)"
                        :key="'cat-' + ci + '-' + (cat.value || cat)"
                        class="session-card__category-chip"
                      >
                        <i class="fas fa-tag" aria-hidden="true"></i>
                        {{ cat.value || cat.name || cat }}
                      </span>
                    </div>

                    <!-- Subtitle -->
                    <p v-if="session.subtitle" class="session-card__subtitle">
                      {{ session.subtitle }}
                    </p>
                    <p v-if="session.subtitle2" class="session-card__subtitle">
                      {{ session.subtitle2 }}
                    </p>

                    <!-- Meta — track + seats + participants -->
                    <div class="session-card__meta">
                      <div class="session-card__meta-row">
                        <div class="session-card__chip-pair">
                          <span
                            v-if="
                              tracks &&
                              tracks.length > 1 &&
                              session.trackId &&
                              session.trackId !== '0' &&
                              trackById(session.trackId) &&
                              !trackById(session.trackId).hideTrackTitle
                            "
                            class="session-card__meta-item session-card__track-pill"
                            :style="trackPillStyle(trackById(session.trackId))"
                          >
                            <i class="fas fa-door-open" aria-hidden="true"></i>
                            {{ trackById(session.trackId).title }}
                          </span>
                          <span
                            v-if="hasSeatLimit(session)"
                            class="session-card__meta-item session-card__seats-pill"
                            :class="seatClass(session)"
                          >
                            <i class="fas fa-chair" aria-hidden="true"></i>
                            {{ seatLabel(session) }}
                          </span>
                        </div>
                        <!-- Participant count — hidden on the card. -->
                        <span v-show="false" class="session-card__meta-item">
                          <i class="fas fa-user-friends" aria-hidden="true"></i>
                          {{
                            $tc(
                              'Views.Lobby.AgendaSection.participantCount',
                              (session.userIds && session.userIds.length) || 0,
                              {
                                count:
                                  (session.userIds && session.userIds.length) ||
                                  0
                              }
                            )
                          }}
                        </span>
                      </div>
                    </div>

                    <!-- Actions -->
                    <div class="session-card__actions">
                      <button
                        v-if="
                          !isLocked(session, bookSessions) && !isPassed(session)
                        "
                        class="session-card__details-btn"
                        @click="openSessionModal(session)"
                      >
                        {{ $t('Views.Lobby.AgendaSection.viewDetails') }}
                        <i class="fas fa-chevron-right" aria-hidden="true"></i>
                      </button>
                      <button
                        type="button"
                        @click="goToSessionLiveStream(session)"
                        v-if="
                          !session.liveUrl &&
                          !isExternalAgenda &&
                          showGoToMainStage &&
                          checkMainStage &&
                          checkMainStage.options &&
                          checkMainStage.options.mainStageEnabled === true
                        "
                        class="session-card__details-btn"
                      >
                        {{ $t('Views.Lobby.Sections.Program.goTo') }}
                        {{ checkMainStage.options.mainStageLabel }}
                        <i class="fas fa-arrow-right" aria-hidden="true"></i>
                      </button>
                      <SessionDocuments
                        v-if="!isExternalAgenda"
                        :session="session"
                      />
                      <span
                        v-if="isPassed(session)"
                        class="session-card__ended-label"
                      >
                        {{ $t('Views.Lobby.AgendaSection.sessionEnded') }}
                      </span>
                    </div>
                  </div>

                  <!-- Open-session button (bottom-right) → live stream, on
                         every session, like the Main stage page -->
                  <button
                    v-if="!isExternalAgenda && session.liveUrl"
                    class="session-card__play-btn"
                    :class="{
                      'session-card__play-btn--live':
                        isLive(session) && !isPassed(session)
                    }"
                    @click="goToSessionLiveStream(session)"
                    :aria-label="
                      isLive(session) && !isPassed(session)
                        ? $t('Views.Program.joinSessionNow')
                        : $t('Views.Program.openSession')
                    "
                    type="button"
                  >
                    <i class="fas fa-play" aria-hidden="true"></i>
                  </button>

                  <!-- Expand/book toggle button -->
                  <button
                    v-if="
                      isBookingsEnabled &&
                      !isBookSessionLoading &&
                      !isPassed(session)
                    "
                    class="session-card__toggle"
                    :class="{
                      'session-card__toggle--booked': isBooked(
                        session,
                        bookSessions
                      )
                    }"
                    :disabled="
                      isLimitReached(session) &&
                      !isBooked(session, bookSessions)
                    "
                    @click="addOrDeleteBooksSesions(session)"
                    :aria-label="
                      isBooked(session, bookSessions)
                        ? $t('Views.Program.removeFromMyProgram')
                        : $t('Views.Program.addToMyProgram')
                    "
                  >
                    <i
                      :class="
                        isBooked(session, bookSessions)
                          ? 'fas fa-minus'
                          : 'fas fa-plus'
                      "
                      aria-hidden="true"
                    ></i>
                  </button>
                  <span
                    v-else-if="isBookSessionLoading"
                    class="session-card__toggle session-card__toggle--loading"
                  >
                    <i class="fas fa-circle-notch fa-spin" aria-hidden="true" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleItem>
      </CollapsibleList>
    </div>

    <!-- Session detail modal -->
    <div
      v-if="activeSession"
      class="session-modal-backdrop"
      @click.self="closeSessionModal"
    >
      <div class="session-modal">
        <button
          class="session-modal__close"
          @click="closeSessionModal"
          :aria-label="$t('Views.Stage.Agenda.close')"
        >
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
        <div class="session-modal__header">
          <span class="session-modal__time">
            {{ formatAgendaTime(activeSession.startDate) }} –
            {{ formatAgendaTime(activeSession.endDate) }}
          </span>
          <h2 class="session-modal__title">{{ activeSession.title }}</h2>
          <p v-if="activeSession.subtitle" class="session-modal__subtitle">
            {{ activeSession.subtitle }}
          </p>
          <p v-if="activeSession.subtitle2" class="session-modal__subtitle">
            {{ activeSession.subtitle2 }}
          </p>
          <p v-if="speakerNames(activeSession)" class="session-modal__speaker">
            <i class="fas fa-microphone-alt" aria-hidden="true"></i>
            <template v-for="(sp, si) in activeSession.speakers">
              <span :key="'msep-' + si" v-if="si > 0">, </span>
              <button
                :key="sp.id || sp.UserID || sp.name || si"
                type="button"
                class="session-modal__speaker-btn"
                @click.stop="openSpeakerModal(sp)"
              >
                {{ speakerFullName(sp) }}
              </button>
            </template>
          </p>
          <div class="session-modal__meta">
            <span
              v-if="
                tracks &&
                tracks.length > 1 &&
                activeSession.trackId &&
                activeSession.trackId !== '0' &&
                trackById(activeSession.trackId) &&
                !trackById(activeSession.trackId).hideTrackTitle
              "
              class="session-card__track-pill"
              :style="trackPillStyle(trackById(activeSession.trackId))"
            >
              <i class="fas fa-door-open" aria-hidden="true"></i>
              {{ trackById(activeSession.trackId).title }}
            </span>
            <span
              v-if="hasSeatLimit(activeSession)"
              class="session-card__seats-pill"
              :class="seatClass(activeSession)"
            >
              <i class="fas fa-chair" aria-hidden="true"></i>
              {{ seatLabel(activeSession) }}
            </span>
          </div>
        </div>
        <div
          v-if="activeSession.description"
          class="session-modal__description rich-text html-content"
          v-html="activeSession.description"
        ></div>
        <div class="session-modal__footer">
          <button
            v-if="activeSession.liveUrl && !isExternalAgenda"
            class="session-modal__join-btn"
            @click="goToSessionLiveStream(activeSession)"
          >
            <span class="session-card__join-dot"></span>
            {{ $t('Views.Lobby.Sections.Program.goToScene') }}
          </button>
          <button
            v-if="isBookingsEnabled"
            class="session-modal__book-btn"
            :class="{
              'session-modal__book-btn--booked': isBooked(
                activeSession,
                bookSessions
              )
            }"
            @click="addOrDeleteBooksSesions(activeSession)"
            :disabled="isBookSessionLoading"
          >
            <i
              :class="
                isBooked(activeSession, bookSessions)
                  ? 'fas fa-minus-circle'
                  : 'fas fa-plus-circle'
              "
              aria-hidden="true"
            ></i>
            {{
              isBooked(activeSession, bookSessions)
                ? $t('Views.Program.removeFromMyProgram')
                : $t('Views.Program.addToMyProgram')
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CollapsibleList from '@/components/shared/CollapsibleList';
import CollapsibleItem from '@/components/shared/CollapsibleItem';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import bookSessions from '@/store/modules/bookSessions';
import { getPusherInstance } from '@/utils';
import ApiClient from '@/client';
import SpeakerInfoModal from '@/components/SpeakerInfoModal';
import SessionDocuments from '@/components/shared/SessionDocuments';

export default {
  components: {
    CollapsibleList,
    CollapsibleItem,
    SessionDocuments
  },
  created() {
    getPusherInstance({ isReady: true }).bind(`agenda-change`, () => {
      this.fetchItems();
    });
  },
  data() {
    return {
      searchText: '',
      selectedTrackId: null,
      selectedCategory: null,
      roomPickerOpen: false,
      categoryPickerOpen: false,
      activeSession: null,
      isDownloadingPdf: false,
      downloadError: false
    };
  },
  mounted() {
    document.addEventListener('click', this.onDocClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick);
    document.body.classList.remove('overflow-hidden');
  },
  props: {
    caption: { type: String, default: '' },
    tracks: Array,
    bookSessions: Array,
    initiallyActiveDate: String,
    groupItemIndex: Function,
    groupItemIsFirst: Function,
    groupItemIsLast: Function,
    searchAgendasForDateAndTime: Function,
    singleAgendaGroupedByDay: Object,
    lastDateInSingleAgenda: String,
    isLoading: Boolean,
    trackById: Function,
    goToStreamFromSession: Function,
    showGoToMainStage: Boolean,
    locale: String,
    checkMainStage: Object,
    addOrDeleteBooksSesions: Function,
    filterSessions: Function,
    agendaSessionCategories: Array,
    isExternalAgenda: Boolean,
    isBookSessionLoading: Boolean
  },
  methods: {
    ...mapActions('Info', ['setPromotedPage']),
    ...mapMutations('Agendas', ['setAgenda']),
    // ── Filter dropdowns ───────────────────────────────────────────
    toggleRoomPicker() {
      this.roomPickerOpen = !this.roomPickerOpen;
      this.categoryPickerOpen = false;
    },
    toggleCategoryPicker() {
      this.categoryPickerOpen = !this.categoryPickerOpen;
      this.roomPickerOpen = false;
    },
    // Close every popover when clicking outside them.
    onDocClick() {
      if (this.roomPickerOpen) this.roomPickerOpen = false;
      if (this.categoryPickerOpen) this.categoryPickerOpen = false;
    },
    selectedRoomLabel(tracks) {
      if (!this.selectedTrackId) {
        return this.$t('Views.Lobby.AgendaSection.allRooms');
      }
      const list = Array.isArray(tracks) ? tracks : [];
      const t = list.find((x) => x && x.id === this.selectedTrackId);
      return t && t.title
        ? t.title
        : this.$t('Views.Lobby.AgendaSection.allRooms');
    },
    clearFilters() {
      this.selectedTrackId = null;
      this.selectedCategory = null;
    },
    // ── Session detail modal ───────────────────────────────────────
    openSessionModal(session) {
      this.activeSession = session;
      document.body.classList.add('overflow-hidden');
    },
    closeSessionModal() {
      this.activeSession = null;
      document.body.classList.remove('overflow-hidden');
    },
    // Normalize a category entry (string or object) down to its label.
    categoryLabel(cat) {
      if (!cat) return '';
      if (typeof cat === 'string') return cat.trim();
      return String(
        cat.value || cat.name || cat.label || cat.title || ''
      ).trim();
    },
    // Route a session into the NEW live-stream page (not the legacy
    // /stage view). Mirrors Program.vue#joinSession: seed the Stream
    // store so LiveStream.vue renders this session, then push to the
    // `live-stream` route with the session id in the query.
    goToSessionLiveStream(session) {
      if (!session) return;
      // Inside the embedded public agenda iframe, keep the original
      // behaviour — the live-stream route is auth-gated and meaningless
      // within an external embed.
      if (this.isExternalAgenda) {
        if (typeof this.goToStreamFromSession === 'function') {
          this.goToStreamFromSession(session);
        }
        return;
      }
      this.$store.commit('Stream/setStreamUrl', session.liveUrl || '');
      this.$store.commit('Stream/setSessionId', session.id);
      this.$store.commit('Stream/setStreamTitle', session.title || '');
      this.$router
        .push({
          name: 'live-stream',
          query: { session: session.id }
        })
        .catch(() => {});
    },
    openSpeakerModal(speaker) {
      if (!speaker) return;
      document.body.classList.add('overflow-hidden');
      this.$modal.show(
        SpeakerInfoModal,
        { speaker },
        {
          width: '92%',
          height: 'auto',
          adaptive: true,
          minWidth: 300,
          maxWidth: 640
        },
        { closed: () => document.body.classList.remove('overflow-hidden') }
      );
    },
    speakerNames(session) {
      if (!session || !session.speakers || !session.speakers.length) return '';
      return session.speakers
        .map((s) => `${s.name || ''} ${s.lastName || ''}`.trim())
        .filter(Boolean)
        .join(', ');
    },
    handleDownloadAgenda() {
      if (this.isDownloadingPdf) {
        return;
      }

      this.isDownloadingPdf = true;
      this.downloadError = false;

      ApiClient.exportAgendaPdf(this.isExternalAgenda)
        .catch(() => {
          this.downloadError = true;
        })
        .finally(() => {
          this.isDownloadingPdf = false;
        });
    },
    fetchItems() {
      if (this.isExternalAgenda) {
        ApiClient.getIframeAgendas().then((r) => this.setAgenda(r.data));
      } else {
        ApiClient.getAgendas().then((r) => this.setAgenda(r.data));
      }
    },
    formatAgendaTime(date) {
      if (!date) return '';

      // STATIC → event timezone
      if (this.agendaTimezoneMode === 2) {
        return new Intl.DateTimeFormat('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: this.eventTimezone
        }).format(new Date(date));
      }

      // DYNAMIC → browser timezone
      return this.$moment.utc(date).local().format('HH:mm');
    },
    // ── Speakers / categories ──────────────────────────────────────
    speakerFullName(sp) {
      if (!sp) return '';
      return `${sp.name || ''}${sp.lastName ? ' ' + sp.lastName : ''}`.trim();
    },
    sessionCategories(session) {
      if (!session) return [];
      const raw = session.categories || session.Categories || session.category;
      if (!raw) return [];
      const list = Array.isArray(raw) ? raw : [raw];
      return list.filter((c) => {
        if (!c) return false;
        if (typeof c === 'string') return c.trim().length > 0;
        const label = c.value || c.name || c.label || c.title;
        return label && String(label).trim().length > 0;
      });
    },
    // ── Track pill colouring ───────────────────────────────────────
    trackPillStyle(track) {
      const bg = (track && track.headerBackgroundColor) || '';
      if (!bg) return null;
      return {
        backgroundColor: bg,
        color: this.pickContrastingTextColor(bg),
        borderColor: bg
      };
    },
    pickContrastingTextColor(bg) {
      const hex = String(bg).trim().replace(/^#/, '');
      const full =
        hex.length === 3
          ? hex
              .split('')
              .map((c) => c + c)
              .join('')
          : hex.length === 6
          ? hex
          : null;
      if (!full) return '#ffffff';
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return lum > 0.62 ? '#111827' : '#ffffff';
    },
    // ── Seat availability ──────────────────────────────────────────
    hasSeatLimit(session) {
      return !!(session && session.myAgendaSize && session.myAgendaSize > 0);
    },
    seatsBookedCount(session) {
      return (session && session.userIds && session.userIds.length) || 0;
    },
    seatsLeftCount(session) {
      return Math.max(
        0,
        (session.myAgendaSize || 0) - this.seatsBookedCount(session)
      );
    },
    seatLabel(session) {
      const left = this.seatsLeftCount(session);
      const total = session.myAgendaSize || 0;
      if (left <= 0) return this.$t('Views.Lobby.AgendaSection.seatsFull');
      return this.$tc('Views.Lobby.AgendaSection.seatsLeft', left, {
        left,
        total
      });
    },
    seatClass(session) {
      const left = this.seatsLeftCount(session);
      const total = session.myAgendaSize || 0;
      if (left <= 0) return 'session-card__seats--full';
      if (total > 0 && left / total <= 0.2) return 'session-card__seats--low';
      return 'session-card__seats--ok';
    },
    // ── Booking / status state ─────────────────────────────────────
    isBooked(session, bookSessions) {
      return bookSessions && bookSessions.some((b) => b.id === session.id);
    },
    isLocked(session, bookSessions) {
      if (!session.myAgendaSize || session.myAgendaSize === 0) return false;
      const currentUserBooked =
        session.userIds &&
        session.userIds.some((id) => id === this.getUser?.UserID);
      return (
        !currentUserBooked &&
        session.userIds &&
        session.userIds.length >= session.myAgendaSize
      );
    },
    sessionMoment(date) {
      if (!date) return null;
      const hasOffset = /Z|[+-]\d{2}:?\d{2}$/.test(String(date));
      if (hasOffset) return this.$moment(date);
      if (this.eventTimezone && this.$moment.tz) {
        return this.$moment.tz(date, this.eventTimezone);
      }
      return this.$moment(date);
    },
    isPassed(session) {
      if (!session || !session.endDate) return false;
      const end = this.sessionMoment(session.endDate);
      return end && end.isBefore(this.$moment());
    },
    // Live — now falls between the session's start and end.
    isLive(session) {
      if (!session || !session.startDate || !session.endDate) return false;
      const now = this.$moment();
      const start = this.sessionMoment(session.startDate);
      const end = this.sessionMoment(session.endDate);
      if (!start || !end) return false;
      return now.isSameOrAfter(start) && now.isBefore(end);
    },
    getStatusLabel(session, bookSessions) {
      if (this.isBooked(session, bookSessions)) {
        return this.$t('Views.Lobby.AgendaSection.statusAttending');
      }
      if (this.isPassed(session)) {
        return this.$t('Views.Lobby.AgendaSection.statusEnded');
      }
      if (this.isLocked(session, bookSessions)) {
        return this.$t('Views.Lobby.AgendaSection.statusLocked');
      }
      return this.$t('Views.Lobby.AgendaSection.statusAvailable');
    },
    getStatusClass(session, bookSessions) {
      if (this.isBooked(session, bookSessions))
        return 'session-card__status--attending';
      if (this.isPassed(session)) return 'session-card__status--ended';
      if (this.isLocked(session, bookSessions))
        return 'session-card__status--locked';
      return 'session-card__status--available';
    }
  },
  computed: {
    ...mapGetters('Authentication', ['getUser']),
    ...mapGetters('CurrentEvent', ['getEvent']),

    applicablePlatform() {
      return this.$route.fullPath.includes('webapp') ? ['webApp'] : ['digital'];
    },
    isLimitReached() {
      return (session) => {
        const checkUser = session.userIds.some(
          (userId) => userId === this.getUser.UserID
        );
        return (
          !checkUser &&
          session.userIds.length === session.myAgendaSize &&
          session.myAgendaSize !== 0
        );
      };
    },
    isBookingsEnabled() {
      const event = this.getEvent;
      return this.isExternalAgenda === true
        ? false
        : event.features.bookingsEnabled;
    },
    selectedCategoryLabel() {
      return (
        this.selectedCategory ||
        this.$t('Views.Lobby.AgendaSection.allCategories')
      );
    },
    // Unique, alphabetized list of category labels across the agenda —
    // drives the Category dropdown.
    allCategories() {
      const sessions =
        (this.singleAgendaGroupedByDay &&
          this.singleAgendaGroupedByDay.sessions) ||
        {};
      const seen = new Set();
      Object.values(sessions).forEach((day) => {
        (day || []).forEach((s) => {
          this.sessionCategories(s).forEach((c) => {
            const label = this.categoryLabel(c);
            if (label) seen.add(label);
          });
        });
      });
      return Array.from(seen).sort((a, b) => a.localeCompare(b));
    },
    // Day-grouped sessions after applying the room / category / search
    // filters. Empty days are dropped so no bare day header shows.
    displayedItems() {
      const base =
        (this.singleAgendaGroupedByDay &&
          this.singleAgendaGroupedByDay.sessions) ||
        {};
      const q = this.searchText.trim().toLowerCase();
      const out = {};
      Object.keys(base)
        .sort()
        .forEach((date) => {
          let list = (base[date] || []).slice();

          if (this.selectedTrackId) {
            list = list.filter((s) => s.trackId === this.selectedTrackId);
          }
          if (this.selectedCategory) {
            list = list.filter((s) =>
              this.sessionCategories(s)
                .map((c) => this.categoryLabel(c))
                .includes(this.selectedCategory)
            );
          }
          if (q) {
            list = list.filter((s) => {
              const inTitle = (s.title || '').toLowerCase().includes(q);
              const inSubtitle = (s.subtitle || '').toLowerCase().includes(q);
              const inSpeaker = this.speakerNames(s).toLowerCase().includes(q);
              const inKeywords = (s.keywords || []).some((k) =>
                (k.value || '').toLowerCase().includes(q)
              );
              return inTitle || inSubtitle || inSpeaker || inKeywords;
            });
          }
          if (list.length) {
            list.sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
            out[date] = list;
          }
        });
      return { sessions: out };
    },
    // The last day that still has visible sessions — used to drop the
    // bottom divider on the final day group.
    lastVisibleDate() {
      const keys = Object.keys(this.displayedItems.sessions);
      return keys.length ? keys[keys.length - 1] : null;
    },
    agendaTimezoneMode() {
      const mode = this.getEvent?.agendaTimezoneMode;
      return mode;
    },
    eventTimezone() {
      const tz = this.getEvent?.timezone;
      return tz;
    }
  }
};
</script>

<style scoped lang="scss">
// ── Section chrome — identical to FAQ / Speakers / Sponsors so all
//    lobby blocks read as one uniform set. ───────────────────────────
.agenda-section {
  font-family: 'Poppins', sans-serif;
  padding-top: 32px;
  padding-bottom: 32px;

  @media (max-width: 640px) {
    padding-top: 20px;
    padding-bottom: 20px;
  }

  &__card {
    background: #ffffff;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid #e5e7eb;

    @media (max-width: 640px) {
      padding: 18px 16px;
    }
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  &__loading {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 40px 0;
    color: var(--color-primary, #047873);
    font-size: 24px;
  }

  &__download-error {
    margin: -8px 0 16px;
    font-size: 13px;
    color: #b91c1c;
  }
}

// ── Filter row (ported from the Program page) ──────────────────────
.prog-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 10px;
  column-gap: 18px;
  padding: 0 0 16px;
  margin-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;

  @media (max-width: 640px) {
    column-gap: 16px;
    row-gap: 16px;
  }
}

.prog-filter {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  position: relative;
  font-family: 'Poppins', sans-serif;

  @media (max-width: 640px) {
    gap: 6px;
  }

  &--search {
    position: relative;
    flex: 0 1 320px;
    min-width: 200px;

    @media (max-width: 640px) {
      flex-basis: 100%;
      min-width: 0;
    }
  }

  &--first-pill {
    @media (min-width: 641px) {
      margin-left: auto;
    }
  }

  &__label {
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    white-space: nowrap;
  }

  &__trigger {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 7px 14px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 9999px;
    cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;
    -webkit-tap-highlight-color: transparent;

    @media (max-width: 640px) {
      padding: 5px 10px;
      gap: 6px;
    }

    &:hover,
    &:focus-visible,
    &--open {
      border-color: #d1d5db;
    }
  }

  &__value {
    font-size: 13px;
    font-weight: 500;
    color: #3d4151;
    line-height: 1.3;
    white-space: nowrap;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (max-width: 640px) {
      max-width: 110px;
    }
  }

  &__chevron {
    color: #6b7280;
    font-size: 12px;
    flex-shrink: 0;
    transition: transform 0.2s, color 0.15s;

    &--open {
      color: var(--color-primary, #047873);
      transform: rotate(180deg);
    }

    @media (max-width: 640px) {
      font-size: 10px;
    }
  }

  &__panel {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: auto;
    z-index: 50;
    min-width: 200px;
    max-width: min(320px, 92vw);
    background: #ffffff;
    border: 1px solid #e5e7eb;
    border-radius: 14px;
    box-shadow: 0 10px 20px -4px rgb(0 0 0 / 0.1),
      0 4px 8px -4px rgb(0 0 0 / 0.08);
    padding: 6px;
    max-height: 280px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2px;

    @media (max-width: 640px) {
      min-width: 180px;
    }
  }

  &--category &__panel {
    left: auto;
    right: 0;
  }

  &__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    padding: 10px 12px;
    border: none;
    background: transparent;
    border-radius: 10px;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    text-align: left;
    transition: background 0.12s, color 0.12s;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;

    &:hover {
      background: #f3f4f6;
    }

    &--active {
      background: #e6f5f4;
      color: var(--color-primary, #047873);
      font-weight: 700;

      &:hover {
        background: #e6f5f4;
      }
    }

    @media (max-width: 640px) {
      padding: 10px;
      font-size: 13px;
    }
  }

  &__option-check {
    color: var(--color-primary, #047873);
    font-size: 12px;
    flex-shrink: 0;
  }

  &__search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    font-size: 13px;
    pointer-events: none;
  }

  &__search {
    width: 100%;
    padding: 10px 16px 10px 38px;
    background: #ffffff;
    border: 1.5px solid #d1d5db;
    border-radius: 9999px;
    font-size: 14px;
    font-weight: 500;
    color: #111827;
    font-family: 'Poppins', sans-serif;
    outline: none;
    line-height: 1.3;
    transition: border-color 0.15s, box-shadow 0.15s;

    &::placeholder {
      color: #6b7280;
      font-weight: 400;
    }

    &:hover {
      border-color: #9ca3af;
    }

    &:focus {
      border-color: var(--color-primary, #047873);
      box-shadow: 0 0 0 3px rgba(4, 120, 115, 0.15);
    }

    @media (max-width: 640px) {
      padding: 11px 16px 11px 38px;
      font-size: 14px;
    }
  }

  &__clear,
  &__download {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 40px;
    padding: 0 12px;
    border-radius: 10px;
    border: 1.5px solid transparent;
    background: rgba(4, 120, 115, 0.08);
    color: var(--color-primary, #047873);
    font-size: 12px;
    font-weight: 700;
    font-family: 'Poppins', sans-serif;
    cursor: pointer;
    flex-shrink: 0;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    outline: none;
    transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;

    &:hover {
      background: rgba(4, 120, 115, 0.14);
      border-color: rgba(4, 120, 115, 0.25);
    }

    &:focus-visible {
      border-color: var(--color-primary, #047873);
      box-shadow: 0 0 0 3px rgba(4, 120, 115, 0.2);
    }
  }

  // Pushed to the far right of the filter row.
  &__download {
    margin-left: auto;
  }
}

// ── Day group ──────────────────────────────────────────────────────
.agenda-day {
  border-bottom: 1px solid #e5e7eb;

  &--last {
    border-bottom: none;
  }

  &__toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 18px 2px;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: color 0.15s;

    &:hover {
      color: var(--color-primary, #111827);
    }
  }

  &__date {
    font-size: 16px;
    font-weight: 600;
    color: #111827;
    text-transform: capitalize;

    @media (max-width: 640px) {
      font-size: 15px;
    }
  }

  &__chevron {
    font-size: 14px;
    font-weight: 700;
    color: #6b7280;
  }

  &__sessions {
    padding-bottom: 16px;
  }
}

// ── Session cards (ported from the Program page so the lobby agenda
//    matches the platform's session-card design) ────────────────────
.session-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}

.session-card {
  display: flex;
  align-items: stretch;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  position: relative;
  min-width: 0;

  &--ended {
    background: #f9fafb;
    opacity: 0.78;

    .session-card__title,
    .session-card__start {
      color: #6b7280;
    }

    .session-card__end,
    .session-card__meta-item {
      color: #6b7280;
    }

    .session-card__clock {
      color: #6b7280;
    }

    .session-card__status {
      background: #f3f4f6 !important;
      color: #6b7280 !important;
      border-color: #e5e7eb !important;
    }
  }

  &__ended-label {
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    background: #f3f4f6;
    color: #6b7280;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
  }

  &__time-col {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 20px 16px;
    min-width: 110px;
    border-right: 1px solid #f3f4f6;
    gap: 4px;

    @media (max-width: 480px) {
      padding: 16px 12px;
      min-width: 92px;
    }
  }

  &__clock {
    font-size: 13px;
    color: var(--color-primary, #047873);
    margin-bottom: 4px;
  }

  &__start {
    font-size: 18px;
    font-weight: 700;
    color: #111827;
    line-height: 1;
  }

  &__end {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.2;
    margin-bottom: 8px;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 9999px;
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;

    &--available {
      border: 1.5px solid var(--color-primary, #047873);
      color: var(--color-primary, #047873);
      background: transparent;
    }

    &--attending {
      background: var(--color-primary, #047873);
      color: #ffffff;
    }

    &--locked {
      border: 1.5px solid #6b7280;
      color: #6b7280;
      background: transparent;
    }

    &--ended {
      border: 1.5px solid #d1d5db;
      color: #6b7280;
      background: transparent;
    }
  }

  &__body {
    flex: 1;
    padding: 18px 64px 18px 22px;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (max-width: 480px) {
      padding: 14px 54px 14px 14px;
    }
  }

  &__title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin: 0;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0;
    line-height: 1.3;
  }

  &__subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0;
  }

  &__category-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    background: #e6f5f4;
    color: var(--color-primary, #047873);
    border: 1px solid transparent;
    border-radius: 9999px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    line-height: 1;
    flex-shrink: 0;

    i {
      font-size: 8px;
      opacity: 0.85;
    }
  }

  &__speakers {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 14px 4px 4px;
    background: #f3f4f6;
    border: 1.5px solid #e5e7eb;
    border-radius: 9999px;
    max-width: 100%;
    align-self: flex-start;
  }

  &__speaker-mic-lead {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: var(--color-primary, #047873);
    color: #ffffff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
  }

  &__speakers-names {
    font-size: 12px;
    font-weight: 400;
    color: #111827;
    line-height: 1.2;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__speakers-sep {
    color: #6b7280;
    margin: 0 2px 0 0;
  }

  &__seats-pill {
    padding: 3px 10px;
    border-radius: 9999px;
    font-weight: 600;
    font-size: 12.5px;
    line-height: 1.4;
    border: 1px solid transparent;
    gap: 6px;

    i {
      font-size: 11px;
      color: inherit;
    }

    &.session-card__seats--ok {
      background: rgba(21, 128, 61, 0.12);
      color: #15803d;
      border-color: rgba(21, 128, 61, 0.25);
    }
    &.session-card__seats--low {
      background: rgba(180, 83, 9, 0.12);
      color: #b45309;
      border-color: rgba(180, 83, 9, 0.3);
    }
    &.session-card__seats--full {
      background: rgba(220, 38, 38, 0.12);
      color: #dc2626;
      border-color: rgba(220, 38, 38, 0.3);
    }
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    column-gap: 16px;
    row-gap: 6px;
    align-items: center;
  }

  &__meta-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    column-gap: 10px;
    row-gap: 6px;
    min-width: 0;

    @media (max-width: 480px) {
      column-gap: 8px;
    }

    .session-card__meta-item {
      white-space: nowrap;
      flex-shrink: 0;
    }

    // Mobile: long track names (e.g. parallel sessions) wrap inside the
    // pill instead of being clipped by the card's `overflow: hidden`.
    @media (max-width: 640px) {
      .session-card__track-pill {
        white-space: normal;
        flex-shrink: 1;
        min-width: 0;
        max-width: 100%;
        overflow-wrap: anywhere;
        align-items: flex-start;
        border-radius: 14px;

        i {
          flex-shrink: 0;
          margin-top: 3px;
        }
      }
    }
  }

  &__chip-pair {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    min-width: 0;

    @media (max-width: 640px) {
      max-width: 100%;
    }
  }

  &__meta-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    color: #6b7280;

    i {
      font-size: 12px;
    }
  }

  &__track-pill {
    padding: 3px 10px;
    border-radius: 9999px;
    font-weight: 600;
    font-size: 12.5px;
    line-height: 1.4;
    border: 1px solid transparent;
    gap: 6px;

    i {
      font-size: 11px;
      color: inherit;
    }
  }

  &__description {
    font-size: 14px;
    color: #374151;
    line-height: 1.7;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    justify-content: flex-start;
    margin-top: auto;
  }

  &__details-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    background: #ffffff;
    color: var(--color-primary, #047873);
    border: 1.5px solid #e5e7eb;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;

    i {
      font-size: 10px;
    }

    &:hover {
      background: #f9fafb;
      border-color: var(--color-primary, #047873);
    }
  }

  // Circular green Join button — anchored bottom-right
  // Small green dot marking a session that is live right now.
  &__live-dot {
    display: inline-block;
    width: 9px;
    height: 9px;
    margin-right: 6px;
    border-radius: 50%;
    background: #16a34a;
    box-shadow: 0 0 0 3px rgba(22, 163, 74, 0.2);
    vertical-align: middle;
    animation: pulse-live 1.6s ease-in-out infinite;
  }

  &__play-btn {
    position: absolute;
    right: 16px;
    bottom: 16px;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 1px solid rgba(22, 163, 74, 0.35);
    background: rgba(22, 163, 74, 0.1);
    color: #16a34a;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    box-shadow: none;
    transition: background 0.15s, transform 0.15s;
    z-index: 2;

    i {
      margin-left: 2px;
    }

    &:hover {
      background: rgba(22, 163, 74, 0.2);
      transform: scale(1.05);
    }

    &:focus-visible {
      outline: 3px solid rgba(22, 163, 74, 0.4);
      outline-offset: 2px;
    }

    @media (max-width: 480px) {
      width: 32px;
      height: 32px;
      right: 12px;
      bottom: 12px;
      font-size: 11px;
    }
  }

  &__toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    flex-shrink: 0;
    align-self: flex-start;
    margin: 16px 16px 0 0;
    font-size: 14px;
    transition: background 0.15s, color 0.15s;

    @media (max-width: 640px) {
      width: 26px;
      height: 26px;
      border-radius: 6px;
      margin: 12px 12px 0 0;
      font-size: 11px;
    }

    &:hover {
      background: #e5e7eb;
    }

    &--booked {
      background: var(--color-primary, #047873);
      color: #ffffff;

      &:hover {
        opacity: 0.85;
        background: var(--color-primary, #047873);
      }
    }

    &--loading {
      cursor: default;
      color: var(--color-primary, #047873);

      &:hover {
        background: #f3f4f6;
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

// ── Session detail modal (ported from the Program page) ────────────
.session-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.session-modal {
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 28px;
  font-family: 'Poppins', sans-serif;

  &__close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;

    &:hover {
      background: #e5e7eb;
    }
  }

  &__header {
    margin-bottom: 20px;
    padding-right: 32px;
  }

  &__time {
    font-size: 14px;
    color: var(--color-primary, #047873);
    font-weight: 600;
    display: block;
    margin-bottom: 8px;
  }

  &__title {
    font-size: 20px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 6px;
  }

  &__subtitle {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 10px;
  }

  &__speaker {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: #374151;
    margin-bottom: 10px;

    i {
      color: #6b7280;
    }
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px 16px;
    align-items: center;
    font-size: 14px;
    color: #6b7280;

    span {
      display: inline-flex;
      align-items: center;
      gap: 5px;

      i {
        font-size: 12px;
      }
    }
  }

  &__description {
    font-size: 14px;
    color: #374151;
    line-height: 1.7;
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
    margin-bottom: 20px;
  }

  &__footer {
    border-top: 1px solid #e5e7eb;
    padding-top: 16px;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
  }

  &__join-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 22px;
    background: #16a34a;
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    font-family: 'Poppins', sans-serif;
    transition: opacity 0.15s;

    &:hover {
      opacity: 0.9;
    }
  }

  &__book-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 24px;
    background: var(--color-primary, #047873);
    color: #ffffff;
    border: none;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;
    font-family: 'Poppins', sans-serif;

    &:hover {
      opacity: 0.85;
    }

    &--booked {
      background: #f3f4f6;
      color: #374151;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}

.session-card__join-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.35);
  animation: pulse-live 1.6s ease-in-out infinite;
}

@keyframes pulse-live {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.4);
    opacity: 0.6;
  }
}

// ── Responsive embedded video in descriptions ──────────────────────
::v-deep .html-content > p:has(iframe) {
  position: relative;
  padding-top: 56.25%;
}
::v-deep .html-content > p > iframe {
  position: absolute;
  width: 100%;
  top: 0;
  height: 100%;
  left: 0;
}

.session-modal__speaker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0 6px;
}
.session-modal__speaker-btn {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 600;
  color: var(--color-primary, #047873);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover,
  &:focus-visible {
    text-decoration-thickness: 2px;
    outline: none;
  }
}
</style>
